// src/utils/axios.js
import axios from 'axios'
import toast from 'react-hot-toast'
import { captureError } from '@/utils/sentry'

// VITE_API_URL is the API host only (e.g. https://dev-api.educza.com).
// Routes live under the 'api' prefix + 'v1' group (backend bootstrap/app.php
// apiPrefix:'api' and routes/api.php Route::prefix('v1')), so all calls go to
// /api/v1/...  e.g. api.post('/auth/register') -> {host}/api/v1/auth/register.
const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL || ''}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 15000,
})

// ─── REQUEST INTERCEPTOR ─────────────────────────────────────────────────────
// Automatically attach the Sanctum token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('educza_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ─── RESPONSE INTERCEPTOR ────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status  = error.response?.status
    const message = error.response?.data?.message

    if (status === 401) {
      // Token expired or invalid — clear and redirect to login
      localStorage.removeItem('educza_token')
      localStorage.removeItem('educza_user')
      window.location.href = '/login'
      return Promise.reject(error)
    }

    if (status === 402) {
      // Plan restriction — show upgrade nudge
      const upgradeUrl = error.response?.data?.upgrade_url
      toast.error(message || 'Upgrade your plan to use this feature.', {
        duration: 5000,
        icon: '⭐',
      })
      if (upgradeUrl) {
        setTimeout(() => window.location.href = upgradeUrl, 2000)
      }
      return Promise.reject(error)
    }

    if (status === 403) {
      toast.error(message || 'You do not have permission to do this.')
      return Promise.reject(error)
    }

    if (status === 422) {
      // Validation errors — let the calling code handle these
      return Promise.reject(error)
    }

    if (status === 500) {
      toast.error('Something went wrong on our end. Please try again.')
      captureError(error, { status, url: error.config?.url, method: error.config?.method })
    }

    return Promise.reject(error)
  }
)

export default api
